import React, { useState } from 'react';
import { RefreshCw, Award, TrendingUp, Download, FileText } from 'lucide-react';
import { jsPDF } from 'jspdf';
import Papa from 'papaparse';
import { format } from 'date-fns';
import { useAuth } from '../../../hooks/useAuth';

export default function ScoringDisplay({ sessionData, selectedTrack, onRestart }) {
  const { userProfile } = useAuth();
  const [isExporting, setIsExporting] = useState(false);

  const getGradeColor = (grade) => {
    switch (grade) {
      case 'A': return 'text-green-400';
      case 'B': return 'text-blue-400';
      case 'C': return 'text-yellow-400';
      case 'D': return 'text-orange-400';
      default: return 'text-red-400';
    }
  };

  // Export to PDF
  const exportToPDF = async () => {
    setIsExporting(true);
    try {
      const doc = new jsPDF();
      const timestamp = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
      
      // Header
      doc?.setFontSize(20);
      doc?.setTextColor(110, 50, 185); // Purple color
      doc?.text('🎤 Pitch Coach Report', 20, 20);
      
      // Student info
      doc?.setFontSize(12);
      doc?.setTextColor(0, 0, 0);
      doc?.text(`Student: ${userProfile?.full_name || userProfile?.username || 'Unknown'}`, 20, 35);
      doc?.text(`Date: ${timestamp}`, 20, 42);
      doc?.text(`Song: ${selectedTrack?.title || 'Unknown Track'}`, 20, 49);
      
      // Grade section
      doc?.setFontSize(18);
      doc?.setTextColor(110, 50, 185);
      doc?.text('Performance Grade', 20, 65);
      
      doc?.setFontSize(36);
      const gradeColor = getGradeColorRGB(sessionData?.grade);
      doc?.setTextColor(gradeColor?.r, gradeColor?.g, gradeColor?.b);
      doc?.text(sessionData?.grade, 20, 80);
      
      doc?.setFontSize(14);
      doc?.setTextColor(0, 0, 0);
      doc?.text(`${sessionData?.pitchAccuracy}% Accuracy`, 20, 90);
      
      // Director's note
      doc?.setFontSize(10);
      doc?.setTextColor(100, 100, 100);
      const splitNote = doc?.splitTextToSize(sessionData?.directorNote, 170);
      doc?.text(splitNote, 20, 100);
      
      // Statistics section
      doc?.setFontSize(16);
      doc?.setTextColor(110, 50, 185);
      doc?.text('Session Statistics', 20, 125);
      
      doc?.setFontSize(11);
      doc?.setTextColor(0, 0, 0);
      
      // Create statistics table
      const stats = [
        ['Pitch Accuracy', `${sessionData?.pitchAccuracy}%`],
        ['Mean Error', `${sessionData?.meanCentsError}¢`],
        ['Note Hit Rate', `${(sessionData?.noteHitRate * 100)?.toFixed(0)}%`],
        ['Longest Streak', `${sessionData?.longestStreak} notes`]
      ];
      
      let yPos = 135;
      stats?.forEach(([label, value]) => {
        doc?.text(`${label}:`, 25, yPos);
        doc?.text(value, 120, yPos);
        yPos += 8;
      });
      
      // Note breakdown section
      doc?.setFontSize(16);
      doc?.setTextColor(110, 50, 185);
      doc?.text('Note Breakdown', 20, yPos + 15);
      
      doc?.setFontSize(11);
      doc?.setTextColor(0, 0, 0);
      yPos += 25;
      
      // Calculate note breakdown (placeholder - would come from actual session data)
      const totalNotes = Math.round(sessionData?.noteHitRate * 100);
      const perfectNotes = Math.round(totalNotes * (sessionData?.pitchAccuracy / 100));
      const goodNotes = totalNotes - perfectNotes;
      const missedNotes = 100 - totalNotes;
      
      doc?.text(`Perfect Notes: ${perfectNotes}`, 25, yPos);
      doc?.text(`Good Notes: ${goodNotes}`, 25, yPos + 8);
      doc?.text(`Missed Notes: ${missedNotes}`, 25, yPos + 16);
      
      // Streak analysis
      doc?.setFontSize(16);
      doc?.setTextColor(110, 50, 185);
      doc?.text('Streak Analysis', 20, yPos + 35);
      
      doc?.setFontSize(11);
      doc?.setTextColor(0, 0, 0);
      doc?.text(`Longest Streak: ${sessionData?.longestStreak} notes in a row`, 25, yPos + 45);
      doc?.text(`Consistency: ${sessionData?.pitchAccuracy >= 90 ? 'Excellent' : sessionData?.pitchAccuracy >= 80 ? 'Good' : sessionData?.pitchAccuracy >= 70 ? 'Fair' : 'Needs Improvement'}`, 25, yPos + 53);
      
      // Footer
      doc?.setFontSize(8);
      doc?.setTextColor(150, 150, 150);
      doc?.text('Generated by Pitch Coach Tool - RoleCall', 20, 280);
      doc?.text(timestamp, 20, 285);
      
      // Save PDF
      const fileName = `pitch-coach-report-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.pdf`;
      doc?.save(fileName);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    setIsExporting(true);
    try {
      const timestamp = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
      
      // Calculate note breakdown
      const totalNotes = Math.round(sessionData?.noteHitRate * 100);
      const perfectNotes = Math.round(totalNotes * (sessionData?.pitchAccuracy / 100));
      const goodNotes = totalNotes - perfectNotes;
      const missedNotes = 100 - totalNotes;
      
      // Prepare CSV data
      const csvData = [
        ['Pitch Coach Report'],
        [''],
        ['Student Information'],
        ['Student Name', userProfile?.full_name || userProfile?.username || 'Unknown'],
        ['Date', timestamp],
        ['Song', selectedTrack?.title || 'Unknown Track'],
        [''],
        ['Performance Summary'],
        ['Grade', sessionData?.grade],
        ['Pitch Accuracy', `${sessionData?.pitchAccuracy}%`],
        ['Director Note', sessionData?.directorNote],
        [''],
        ['Session Statistics'],
        ['Mean Error (cents)', sessionData?.meanCentsError],
        ['Note Hit Rate', `${(sessionData?.noteHitRate * 100)?.toFixed(0)}%`],
        ['Longest Streak', sessionData?.longestStreak],
        [''],
        ['Note Breakdown'],
        ['Perfect Notes', perfectNotes],
        ['Good Notes', goodNotes],
        ['Missed Notes', missedNotes],
        ['Total Notes Attempted', 100],
        [''],
        ['Streak Analysis'],
        ['Longest Streak (notes)', sessionData?.longestStreak],
        ['Consistency Rating', sessionData?.pitchAccuracy >= 90 ? 'Excellent' : sessionData?.pitchAccuracy >= 80 ? 'Good' : sessionData?.pitchAccuracy >= 70 ? 'Fair' : 'Needs Improvement']
      ];
      
      // Convert to CSV string
      const csv = Papa?.unparse(csvData);
      
      // Create download link
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link?.setAttribute('href', url);
      link?.setAttribute('download', `pitch-coach-report-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.csv`);
      link.style.visibility = 'hidden';
      
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      
    } catch (error) {
      console.error('Error generating CSV:', error);
      alert('Failed to generate CSV. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Helper function to get RGB color for grade (for PDF)
  const getGradeColorRGB = (grade) => {
    switch (grade) {
      case 'A': return { r: 34, g: 197, b: 94 }; // green-400
      case 'B': return { r: 59, g: 130, b: 246 }; // blue-400
      case 'C': return { r: 250, g: 204, b: 21 }; // yellow-400
      case 'D': return { r: 251, g: 146, b: 60 }; // orange-400
      default: return { r: 239, g: 68, b: 68 }; // red-400
    }
  };

  return (
    <div className="space-y-6">
      {/* Grade Card */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 text-center">
        <div className="mb-4">
          <Award className="w-16 h-16 text-yellow-400 mx-auto" />
        </div>
        <h2 className="text-6xl font-bold mb-2">
          <span className={getGradeColor(sessionData?.grade)}>{sessionData?.grade}</span>
        </h2>
        <p className="text-2xl text-white font-medium mb-4">{sessionData?.pitchAccuracy}% Accuracy</p>
        <p className="text-white/70">{sessionData?.directorNote}</p>
      </div>

      {/* Stats */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-green-400" />
          Session Stats
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-white/5 rounded-xl">
            <p className="text-white/70 text-sm mb-1">Mean Error</p>
            <p className="text-2xl font-bold text-white">{sessionData?.meanCentsError}¢</p>
          </div>
          <div className="p-4 bg-white/5 rounded-xl">
            <p className="text-white/70 text-sm mb-1">Note Hit Rate</p>
            <p className="text-2xl font-bold text-white">{(sessionData?.noteHitRate * 100)?.toFixed(0)}%</p>
          </div>
          <div className="p-4 bg-white/5 rounded-xl">
            <p className="text-white/70 text-sm mb-1">Longest Streak</p>
            <p className="text-2xl font-bold text-white">{sessionData?.longestStreak}</p>
          </div>
          <div className="p-4 bg-white/5 rounded-xl">
            <p className="text-white/70 text-sm mb-1">Song</p>
            <p className="text-white font-medium text-sm">{selectedTrack?.title}</p>
          </div>
        </div>
      </div>

      {/* Export Buttons */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <FileText className="w-6 h-6 text-blue-400" />
          Export Report
        </h3>
        <p className="text-white/70 text-sm mb-4">
          Download your performance report for teacher review, portfolio building, or progress documentation.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={exportToPDF}
            disabled={isExporting}
            className="py-3 px-6 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-600/50 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
          >
            <Download className="w-5 h-5" />
            Export PDF
          </button>
          <button
            onClick={exportToCSV}
            disabled={isExporting}
            className="py-3 px-6 bg-green-600 hover:bg-green-700 disabled:bg-green-600/50 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
          >
            <Download className="w-5 h-5" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Actions */}
      <button
        onClick={onRestart}
        className="w-full py-4 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-bold text-lg transition-colors flex items-center justify-center gap-2"
      >
        <RefreshCw className="w-6 h-6" />
        Practice Again
      </button>
    </div>
  );
}